/**
 * API Response Handling Utilities
 * 
 * Provides utilities for unwrapping and handling the standardized
 * API response envelope format.
 * 
 * Response Envelope Format:
 * {
 *   success: boolean,
 *   data: T | null,
 *   error: ApiError | null,
 *   meta: {
 *     timestamp: string,
 *     page?: number,
 *     pageSize?: number,
 *     totalPages?: number,
 *     totalItems?: number
 *   }
 * }
 */

import { ApiError } from './apiError';

/**
 * Check if response follows the new envelope format
 * @param {Object} data - Response data
 * @returns {boolean}
 */
export const isEnvelopeFormat = (data) => {
  return data && typeof data.success === 'boolean';
};

/**
 * Unwrap API response from the standardized envelope
 * 
 * @param {Object} response - Axios response object
 * @returns {{ data: any, meta: Object }} Unwrapped data and meta
 * @throws {ApiError} If response indicates failure
 */
export const unwrapResponse = (response) => {
  const envelope = response.data;

  // Handle new envelope format
  if (isEnvelopeFormat(envelope)) {
    if (envelope.success) {
      return {
        data: envelope.data,
        meta: envelope.meta || {},
      };
    } else {
      // Throw ApiError for failed responses
      throw new ApiError(envelope.error);
    }
  }

  // Fallback for legacy responses (backward compatibility)
  // During migration, some endpoints may still return raw data
  return {
    data: envelope,
    meta: {},
  };
};

/**
 * Extract just the data from a response (convenience method)
 * @param {Object} response - Axios response object
 * @returns {any} The data payload
 */
export const unwrapData = (response) => {
  const { data } = unwrapResponse(response);
  return data;
};

/**
 * Extract pagination info from meta object
 * @param {Object} meta - Meta object from response
 * @returns {Object} Pagination info with defaults
 */
export const extractPagination = (meta = {}) => ({
  page: meta.page || 1,
  pageSize: meta.pageSize || 20,
  totalPages: meta.totalPages || 1,
  totalItems: meta.totalItems || 0,
  hasNextPage: (meta.page || 1) < (meta.totalPages || 1),
  hasPrevPage: (meta.page || 1) > 1,
});

/**
 * Check if response has more pages
 * @param {Object} meta - Meta object from response
 * @returns {boolean}
 */
export const hasMorePages = (meta = {}) => {
  const { page, totalPages } = extractPagination(meta);
  return page < totalPages;
};

/**
 * Create a paginated response handler
 * Useful for infinite scroll or load more patterns
 * 
 * @param {Function} fetchFn - Async function that fetches data
 * @returns {Object} Paginated handler with state and methods
 */
export const createPaginatedFetcher = (fetchFn) => {
  let currentPage = 1;
  let totalPages = 1;
  let allData = [];
  let isLoading = false;

  return {
    async fetchPage(page = 1) {
      isLoading = true;
      try {
        const response = await fetchFn({ page });
        const { data, meta } = unwrapResponse(response);
        
        currentPage = meta.page || page;
        totalPages = meta.totalPages || 1;
        
        if (page === 1) {
          allData = data;
        } else {
          allData = [...allData, ...data];
        }
        
        return { data: allData, meta, hasMore: currentPage < totalPages };
      } finally {
        isLoading = false;
      }
    },

    async fetchNext() {
      if (currentPage >= totalPages) {
        return { data: allData, hasMore: false };
      }
      return this.fetchPage(currentPage + 1);
    },

    reset() {
      currentPage = 1;
      totalPages = 1;
      allData = [];
    },

    get isLoading() {
      return isLoading;
    },

    get hasMore() {
      return currentPage < totalPages;
    },

    get data() {
      return allData;
    },
  };
};

/**
 * Response transformer for axios interceptor
 * Automatically unwraps envelope and attaches meta to response
 * 
 * @param {Object} response - Axios response
 * @returns {Object} Transformed response with unwrapped data
 */
export const responseTransformer = (response) => {
  try {
    const { data, meta } = unwrapResponse(response);
    // Replace response.data with unwrapped data
    response.data = data;
    // Attach meta for pagination support
    response.meta = meta;
    return response;
  } catch (error) {
    // If unwrapResponse throws ApiError, re-throw it
    throw error;
  }
};

/**
 * Helper to safely access nested data with fallback
 * @param {Object} response - API response
 * @param {string} path - Dot-notation path (e.g., 'user.profile.name')
 * @param {any} fallback - Fallback value if path doesn't exist
 * @returns {any}
 */
export const getResponseData = (response, path = '', fallback = null) => {
  if (!path) {
    return response?.data ?? fallback;
  }

  const data = response?.data;
  if (!data) return fallback;

  const keys = path.split('.');
  let result = data;

  for (const key of keys) {
    if (result === null || result === undefined) {
      return fallback;
    }
    result = result[key];
  }

  return result ?? fallback;
};

export default {
  unwrapResponse,
  unwrapData,
  extractPagination,
  hasMorePages,
  createPaginatedFetcher,
  responseTransformer,
  getResponseData,
  isEnvelopeFormat,
};
